import { computed, watch } from 'vue'
import { useRouteLocale } from './useRouteLocale'

export const DEFAULT_CATEGORY = 'biblical'

export interface IndexRouteParams {
  locale?: string
  category?: string
  slug?: string
  articleSlug?: string
  eventSlug?: string
}

export function buildIndexPath(locale: string): string {
  return `/${locale}`
}

export function buildCoursesPath(
  locale: string,
  category?: string,
  slug?: string
): string {
  if (slug != null && slug !== '' && category != null) {
    return `/${locale}/courses/${category}/${slug}`
  }
  if (category != null && category !== '') {
    return `/${locale}/courses/${category}`
  }
  return `/${locale}/courses`
}

export function buildArticlesPath(locale: string, articleSlug?: string): string {
  if (articleSlug != null && articleSlug !== '') {
    return `/${locale}/articles/${articleSlug}`
  }
  return `/${locale}/articles`
}

export function buildEventsPath(locale: string, eventSlug?: string): string {
  if (eventSlug != null && eventSlug !== '') {
    return `/${locale}/events/${eventSlug}`
  }
  return `/${locale}/events`
}

export function useIndexRoutes() {
  const route = useRoute()
  const router = useRouter()
  const { currentLocale, isRouteLocaleValid } = useRouteLocale()

  function buildPath(params: IndexRouteParams = {}): string {
    const loc = params.locale ?? currentLocale.value
    if (params.eventSlug !== undefined) {
      return buildEventsPath(loc, params.eventSlug)
    }
    if (params.articleSlug !== undefined) {
      return buildArticlesPath(loc, params.articleSlug)
    }
    if (params.category != null || params.slug != null) {
      const cat = params.category ?? (params.slug ? DEFAULT_CATEGORY : undefined)
      return buildCoursesPath(loc, cat, params.slug)
    }
    return buildIndexPath(loc)
  }

  function navigate(params: IndexRouteParams | string): void {
    const path = typeof params === 'string' ? params : buildPath(params)
    router.push(path)
  }

  function setupCategoryPageLoad(loadCategory: () => void | Promise<void>): void {
    const hasSlug = computed(() => !!(route.params.slug as string))

    watch(
      () => route.params.category,
      (newCat, oldCat) => {
        if (newCat && newCat !== oldCat) {
          loadCategory()
        }
      },
    )

    watch(hasSlug, (isSlug, wasSlug) => {
      if (wasSlug && !isSlug) {
        loadCategory()
      }
    })
  }

  function setupCourseDetailPageLoad(loadCourse: () => void | Promise<void>): void {
    watch(
      () => route.params.slug as string,
      (newSlug, oldSlug) => {
        if (newSlug && newSlug !== oldSlug) {
          loadCourse()
        }
      },
    )
  }

  return {
    currentLocale,
    isRouteLocaleValid,
    buildPath,
    buildArticlesPath: (articleSlug?: string) => buildArticlesPath(currentLocale.value, articleSlug),
    buildEventsPath: (eventSlug?: string) => buildEventsPath(currentLocale.value, eventSlug),
    buildCoursesPath: (category?: string, slug?: string) =>
      buildCoursesPath(currentLocale.value, category, slug),
    navigate,
    setupCategoryPageLoad,
    setupCourseDetailPageLoad,
    DEFAULT_CATEGORY,
  }
}
